import { z } from 'zod';
import { careTeamPhotoUrlShape } from './care-photo-url';
import { deriveNameInitials } from './initials';

/** Maximum faces rendered in the overlapping stack before an overflow pill. */
export const WELCOME_POOL_PREVIEW_VISIBLE_FACE_CAP = 3;

export const welcomePoolPreviewFaceSchema = z
  .object({
    initials: z.string().min(1).max(3),
    photoUrl: z
      .string()
      .regex(careTeamPhotoUrlShape, 'photoUrl must be an https URL with a host')
      .optional(),
  })
  .strip();

export type WelcomePoolPreviewFace = z.infer<typeof welcomePoolPreviewFaceSchema>;

export const welcomePoolPreviewSchema = z
  .object({
    faces: z.array(welcomePoolPreviewFaceSchema).max(WELCOME_POOL_PREVIEW_VISIBLE_FACE_CAP),
    overflowCount: z.number().int().nonnegative(),
  })
  .strip();

export type WelcomePoolPreview = z.infer<typeof welcomePoolPreviewSchema>;

/** Stable empty preview for tests and fail-soft paths when the pool cannot be resolved. */
export const EMPTY_WELCOME_POOL_PREVIEW: WelcomePoolPreview = { faces: [], overflowCount: 0 };

export interface WelcomePoolPreviewMember {
  displayName: string;
  photoUrl?: string;
  /** Rendered first, ahead of the display-name order (e.g. the member's own specialist). */
  leading?: boolean;
}

/**
 * Builds the member-facing welcome-pool avatar stack from resolved pool rows.
 * Shows every face when the pool has three or fewer specialists; otherwise the
 * first three (any `leading` member first, then stable display-name order)
 * plus an overflow count for the rest.
 */
export function buildWelcomePoolPreview(
  members: ReadonlyArray<WelcomePoolPreviewMember>,
): WelcomePoolPreview {
  const sorted = [...members].sort(
    (left, right) =>
      Number(right.leading === true) - Number(left.leading === true) ||
      left.displayName.localeCompare(right.displayName),
  );
  const visible = sorted.slice(0, WELCOME_POOL_PREVIEW_VISIBLE_FACE_CAP);
  const faces = visible.map((member) => ({
    initials: deriveNameInitials(member.displayName),
    ...(member.photoUrl ? { photoUrl: member.photoUrl } : {}),
  }));
  return {
    faces,
    overflowCount: Math.max(0, sorted.length - WELCOME_POOL_PREVIEW_VISIBLE_FACE_CAP),
  };
}

/** Member-facing overflow pill copy (e.g. "+12"). */
export function formatWelcomePoolOverflowLabel(overflowCount: number): string {
  return `+${overflowCount}`;
}

/** One circle of the welcome-pool avatar stack, in the design system's face shape. */
export interface WelcomePoolAvatarFace {
  imageUri?: string;
  initials?: string;
}

/**
 * The faces every surface draws for the welcome pool: one per pool face, its
 * photo when it has one and its initials otherwise, so real colleagues are
 * never stood in for by stock imagery. An unresolved pool (no faces) fills
 * every slot with the neutral person glyph, so the stack never renders empty.
 */
export function welcomePoolAvatarFaces(
  preview: WelcomePoolPreview | null | undefined,
): WelcomePoolAvatarFace[] {
  const faces = preview?.faces ?? [];
  if (faces.length === 0) {
    return Array.from({ length: WELCOME_POOL_PREVIEW_VISIBLE_FACE_CAP }, () => ({}));
  }
  // Initials ride along with a photo so a photo that fails to load still names the specialist.
  return faces.map((face) => ({
    initials: face.initials,
    ...(face.photoUrl ? { imageUri: face.photoUrl } : {}),
  }));
}
